import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../../lib/utils";

/**
 * Typography variant styles based on Capsule Design System
 *
 * Display variants use Pfizer Tomorrow font (headings)
 * Text variants use Pfizer Diatype font (body text)
 *
 * @see https://www.figma.com/design/j1q6kG0YaKkt7ASWZLrvE9/Capsule-DS--New-?node-id=15020-5776
 */
const typographyVariants = cva("", {
  variants: {
    /**
     * Typography variant determines font family, size, and line height
     */
    variant: {
      // Display variants - Pfizer Tomorrow font (headings)
      "display-lg":
        "font-tomorrow text-[36px] leading-[44px] tracking-[-0.02em]",
      "display-md": "font-tomorrow text-[32px] leading-[40px]",
      "display-sm": "font-tomorrow text-[28px] leading-[32px]",
      "display-xs": "font-tomorrow text-[24px] leading-[28px]",
      // Text variants - Pfizer Diatype font (body text)
      "text-xl": "font-diatype text-[24px] leading-[30px]",
      "text-lg": "font-diatype text-[18px] leading-[28px]",
      "text-md": "font-diatype text-[16px] leading-[24px]",
      "text-s": "font-diatype text-[14px] leading-[20px]",
      "text-xs": "font-diatype text-[12px] leading-[18px]",
    },
    /**
     * Font weight
     */
    weight: {
      regular: "font-normal",
      medium: "font-medium",
      bold: "font-bold",
    },
    /**
     * Text color using semantic tokens
     */
    color: {
      primary: "text-text-primary",
      secondary: "text-text-secondary",
      tertiary: "text-text-tertiary",
      disabled: "text-text-disabled",
      link: "text-text-link",
      success: "text-text-success",
      warning: "text-text-warning",
      critical: "text-text-critical",
      inverse: "text-text-inverse",
      inherit: "text-inherit",
    },
    /**
     * Text alignment
     */
    align: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
    },
    /**
     * Truncation behavior
     */
    truncate: {
      true: "truncate",
      false: "",
    },
  },
  defaultVariants: {
    variant: "text-md",
    weight: "regular",
    color: "primary",
    align: "left",
    truncate: false,
  },
});

/**
 * Default HTML element mapping for each variant
 */
const defaultElementMap: Record<string, React.ElementType> = {
  "display-lg": "h1",
  "display-md": "h2",
  "display-sm": "h3",
  "display-xs": "h4",
  "text-xl": "p",
  "text-lg": "p",
  "text-md": "p",
  "text-s": "p",
  "text-xs": "span",
};

export interface TypographyProps
  extends
    Omit<React.HTMLAttributes<HTMLElement>, "color">,
    VariantProps<typeof typographyVariants> {
  /**
   * The HTML element to render.
   * Defaults to a semantic element based on the variant (h1-h4 for display, p for text)
   */
  as?: React.ElementType;
}

/**
 * Typography component for consistent text styling across the application.
 *
 * Uses the Capsule Design System typography tokens with Pfizer Tomorrow (display)
 * and Pfizer Diatype (text) font families.
 *
 * @example
 * ```tsx
 * // Display heading
 * <Typography variant="display-lg" weight="bold">Page Title</Typography>
 *
 * // Body text
 * <Typography variant="text-md">Regular paragraph text</Typography>
 *
 * // Custom element
 * <Typography as="label" variant="text-s" weight="medium">Form Label</Typography>
 * ```
 */
function Typography({
  className,
  variant = "text-md",
  weight,
  color,
  align,
  truncate,
  as,
  children,
  ...props
}: TypographyProps) {
  const Component = as || defaultElementMap[variant || "text-md"] || "span";

  return (
    <Component
      className={cn(
        typographyVariants({ variant, weight, color, align, truncate }),
        className,
      )}
      {...props}
    >
      {children}
    </Component>
  );
}

Typography.displayName = "Typography";

export { Typography, typographyVariants };
